/**
 * Piso Vivo — regras da cena do escritório virtual (não é reunião).
 * Sem backend: hora do dia, ritual rotativo e sinais leves no DataTrack.
 */

import { userIdFromAgentIdentity } from "./voOfficeLife";
import {
  DEFAULT_CLOCK_TZ,
  floorJoinCaption,
  formatClockTimeInZone,
  normalizeVoCommStatus,
  voCommStatusShortLabel,
} from "../VirtualOffice/voCommunicationStatus";

export const VO_FLOOR_REACTIONS = ["👋", "☕", "👏", "🎉", "🏆"];

/** Tempo visível no piso (pausa se a aba estiver oculta). */
export const VO_FLOOR_REACTION_HOLD_MS = 45_000;
/** Teto absoluto para não acumular reação esquecida. */
export const VO_FLOOR_REACTION_MAX_AGE_MS = 180_000;
export const VO_FLOOR_REACTION_FADE_MS = 1_600;
export const VO_FLOOR_REACTION_MAX = 16;

export function createVoFloorReaction({
  id,
  emoji,
  fromName,
  fromIdentity,
  x,
  y,
  now = Date.now(),
  hidden = false,
} = {}) {
  const holdMs = VO_FLOOR_REACTION_HOLD_MS;
  return {
    id,
    emoji,
    fromName,
    fromIdentity,
    x,
    y,
    at: now,
    holdMs,
    remainingMs: holdMs,
    until: hidden ? null : now + holdMs,
    fading: false,
  };
}

export function freezeVoFloorReactionTimers(items, now = Date.now()) {
  return (Array.isArray(items) ? items : []).map((item) => {
    if (!item || item.until == null) return item;
    return {
      ...item,
      remainingMs: Math.max(0, Number(item.until) - now),
      until: null,
      fading: false,
    };
  });
}

export function resumeVoFloorReactionTimers(items, now = Date.now()) {
  return (Array.isArray(items) ? items : []).map((item) => {
    if (!item) return item;
    const remaining =
      item.until != null
        ? Math.max(0, Number(item.until) - now)
        : Math.max(0, Number(item.remainingMs) || 0);
    return {
      ...item,
      remainingMs: remaining,
      until: now + remaining,
    };
  });
}

export function shouldKeepVoFloorReaction(item, { now = Date.now(), hidden = false } = {}) {
  if (!item) return false;
  const at = Number(item.at) || now;
  if (now - at >= VO_FLOOR_REACTION_MAX_AGE_MS) return false;
  if (hidden) return true;
  if (item.until != null) return Number(item.until) > now;
  return Math.max(0, Number(item.remainingMs) || 0) > 0;
}

export function voFloorReactionIsFading(item, now = Date.now()) {
  if (!item || item.until == null) return false;
  const left = Number(item.until) - now;
  return left > 0 && left <= VO_FLOOR_REACTION_FADE_MS;
}

export function tickVoFloorReactions(items, { now = Date.now(), hidden = false } = {}) {
  const list = Array.isArray(items) ? items : [];
  if (!list.length) return list;
  const next = [];
  let changed = false;
  for (const item of list) {
    if (!shouldKeepVoFloorReaction(item, { now, hidden })) {
      changed = true;
      continue;
    }
    const fading = !hidden && voFloorReactionIsFading(item, now);
    if (item.fading === fading) {
      next.push(item);
    } else {
      changed = true;
      next.push({ ...item, fading });
    }
  }
  return changed ? next : list;
}

export const VO_FLOOR_PROMPTS = [
  {
    id: "spark",
    question: "O que te animou hoje?",
    answers: ["Uma conversa", "Um café", "Uma entrega", "O silêncio bom"],
  },
  {
    id: "sip",
    question: "Café ou chá?",
    answers: ["Café", "Chá", "Água", "Já tomei demais"],
  },
  {
    id: "win",
    question: "Uma vitória pequena da semana?",
    answers: ["Fechei um ciclo", "Ajudei alguém", "Aprendi algo", "Ainda está por vir"],
  },
  {
    id: "mood",
    question: "Como está o clima do time agora?",
    answers: ["Leve", "Focado", "Acelerado", "Precisa de um café"],
  },
  {
    id: "arrive",
    question: "Como você chegou hoje?",
    answers: ["Disposto", "No ritmo", "Em modo foco", "Ainda aquecendo"],
  },
  {
    id: "share",
    question: "O que você levaria para o café da casa?",
    answers: ["Um caso bom", "Uma piada", "Um pedido de ajuda", "Só a companhia"],
  },
  {
    id: "thanks",
    question: "Quem merece um obrigado hoje?",
    answers: ["Um colega", "O time todo", "Eu mesmo", "Ainda vou escolher"],
  },
];

export const VO_FLOOR_PROMPT_STORAGE_KEY = "xpro.voFloorPrompt.hiddenDate";
/** Twilio Group Room: até 50 participantes, incluindo você. */
export const TWILIO_VIDEO_MAX_PARTICIPANTS = 50;
export const MAX_VO_FLOOR_SEATS = TWILIO_VIDEO_MAX_PARTICIPANTS;
/** Colegas no tapete (o assento local ocupa 1 dos 50). */
export const MAX_VO_FLOOR_OTHERS = TWILIO_VIDEO_MAX_PARTICIPANTS - 1;

export function voFloorFirstName(name, fallback = "Colega") {
  const s = String(name || "").trim();
  if (!s) return fallback;
  return s.split(/\s+/)[0];
}

/** Quem mandou a reação — primeiro nome, para o piso saber de quem é. */
export function reactionSenderLabel(name, fallback = "Alguém") {
  return voFloorFirstName(name, fallback);
}

export function voFloorDaypart(now = new Date()) {
  const hour = now instanceof Date ? now.getHours() : Number(now) || 0;
  if (hour < 12) return "morning";
  if (hour < 17) return "afternoon";
  return "evening";
}

const SCENE_BOOKS = ["#8c3d2f", "#2f4f6d", "#c4a15a", "#3f6b4e", "#6b3a55", "#d4c4a8"];

export function voFloorSceneTokens(daypart = "afternoon") {
  if (daypart === "morning") {
    return {
      daypart,
      sky: "linear-gradient(180deg, #f4d7b0 0%, #e8c9a0 46%, #6a4a34 100%)",
      glow: "radial-gradient(ellipse at 20% 18%, rgba(255,214,150,0.42), transparent 42%)",
      lamp: "radial-gradient(ellipse at 50% 78%, rgba(232,176,92,0.2), transparent 36%)",
      table: "#6b4a2b",
      tableEdge: "#8a6238",
      label: "Manhã no piso",
      hint: "O escritório está acordando",
      window: "linear-gradient(180deg, #f6d7a0 0%, #f3c48a 55%, #e8b070 100%)",
      ceiling: "#f3e4d0",
      wall: "#e6cbaa",
      wallDeep: "#c9ae8c",
      wainscot: "#8a5a3a",
      wainscotDeep: "#6f452c",
      trim: "#5a3822",
      floor: "#7a4e36",
      floorDeep: "#4a2f22",
      floorLine: "#3a241a",
      rug: "#9a4032",
      rugInner: "#c46848",
      glassTop: "#f7d7a4",
      glassBot: "#e59b5c",
      city: "#b88968",
      cityFar: "#d2ae8c",
      cityLit: "#ffe6b3",
      plant: "#2f6b46",
      plantDeep: "#1c4630",
      wood: "#6b4428",
      shelf: "#5a3c28",
      books: SCENE_BOOKS,
      lampShade: "#f0d2a0",
      lampMetal: "#c9a06a",
      shaft: "rgba(255,210,140,0.28)",
      sconce: "rgba(255,200,120,0.08)",
    };
  }
  if (daypart === "evening") {
    return {
      daypart,
      sky: "linear-gradient(180deg, #1b2436 0%, #2a2830 48%, #241c18 100%)",
      glow: "radial-gradient(ellipse at 80% 12%, rgba(120,140,190,0.2), transparent 44%)",
      lamp: "radial-gradient(ellipse at 50% 76%, rgba(255,186,92,0.22), transparent 38%)",
      table: "#4a3424",
      tableEdge: "#6a4a30",
      label: "Fim de expediente",
      hint: "Boa hora de ir fechando o dia",
      window: "linear-gradient(180deg, #141a28 0%, #243044 55%, #3a2e28 100%)",
      ceiling: "#2a2430",
      wall: "#3a322c",
      wallDeep: "#2a241f",
      wainscot: "#4a3428",
      wainscotDeep: "#38261c",
      trim: "#2a1c14",
      floor: "#3d2a20",
      floorDeep: "#241812",
      floorLine: "#1a100c",
      rug: "#6a2e28",
      rugInner: "#8a4034",
      glassTop: "#1a2438",
      glassBot: "#2a3048",
      city: "#1a2030",
      cityFar: "#243044",
      cityLit: "#f0c878",
      plant: "#1f4a34",
      plantDeep: "#123224",
      wood: "#4a3224",
      shelf: "#3a281c",
      books: SCENE_BOOKS,
      lampShade: "#e8c48a",
      lampMetal: "#c9a06a",
      shaft: "rgba(160,180,220,0.1)",
      sconce: "rgba(255,186,92,0.38)",
    };
  }
  return {
    daypart: "afternoon",
    sky: "linear-gradient(180deg, #c5d2d8 0%, #b7c0bc 46%, #5c4030 100%)",
    glow: "radial-gradient(ellipse at 22% 16%, rgba(255,255,255,0.2), transparent 40%)",
    lamp: "radial-gradient(ellipse at 50% 78%, rgba(210,170,110,0.16), transparent 36%)",
    table: "#5c4028",
    tableEdge: "#7a5834",
    label: "Tarde no piso",
    hint: "Ritmo do meio do dia",
    window: "linear-gradient(180deg, #8eb4d4 0%, #c5d4dc 70%, #d8c8b0 100%)",
    ceiling: "#ddd0c2",
    wall: "#d4b89a",
    wallDeep: "#b89a7c",
    wainscot: "#7a4e34",
    wainscotDeep: "#5c3826",
    trim: "#4a2e1e",
    floor: "#6a4430",
    floorDeep: "#3e2a1e",
    floorLine: "#2e1c14",
    rug: "#8a3830",
    rugInner: "#b85a44",
    glassTop: "#8eb6d6",
    glassBot: "#d4c8b4",
    city: "#8a7a6c",
    cityFar: "#b0a090",
    cityLit: "#f2e6c4",
    plant: "#2a6240",
    plantDeep: "#184830",
    wood: "#5c3c28",
    shelf: "#4a3224",
    books: SCENE_BOOKS,
    lampShade: "#e4c896",
    lampMetal: "#b8925c",
    shaft: "rgba(220,230,240,0.16)",
    sconce: "rgba(255,220,160,0.1)",
  };
}

export function voFloorDaypartHint(daypart = "afternoon") {
  if (daypart === "morning") return "O escritório está acordando";
  if (daypart === "evening") return "Boa hora de ir fechando o dia";
  return "Ritmo do meio do dia";
}

export function formatFloorClock(now = new Date(), timeZone = null) {
  const d = now instanceof Date ? now : new Date(now);
  if (Number.isNaN(d.getTime())) return "";
  if (timeZone) return formatClockTimeInZone(d, timeZone);
  return d.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit", hour12: false });
}

/** Uma linha para o parapeito da janela — sem cartão, sem “você no piso”. */
export function floorSillCaption(daypart = "afternoon", now = new Date(), timeZone = null) {
  const clock = formatFloorClock(now, timeZone);
  const label = voFloorSceneTokens(daypart).label || "";
  if (clock && label) return `${clock}  ·  ${label}`;
  return clock || label;
}

export function floorPeopleLabel(count = 0) {
  const n = Math.max(0, Number(count) || 0);
  if (n <= 1) return "Você no piso";
  return `${n} no piso agora`;
}

export function majorityCamerasOn({
  remoteCount = 0,
  remoteCameraOnCount = 0,
  localCamOn = false,
} = {}) {
  const total = Math.max(0, Number(remoteCount) || 0) + 1;
  const on = Math.max(0, Number(remoteCameraOnCount) || 0) + (localCamOn ? 1 : 0);
  return on > total / 2;
}

export function shouldUseVoFloorScene({
  isVoFloor = false,
  presenting = false,
} = {}) {
  return Boolean(isVoFloor) && !presenting;
}

export function promptForDate(now = new Date()) {
  const date = now instanceof Date ? now : new Date(now);
  const start = Date.UTC(date.getFullYear(), 0, 0);
  const day = Math.floor((Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()) - start) / 86400000);
  const list = VO_FLOOR_PROMPTS;
  return list[Math.abs(day) % list.length];
}

export function todayKey(now = new Date()) {
  const d = now instanceof Date ? now : new Date(now);
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${m}-${day}`;
}

export function readVoFloorPromptHidden(storage, now = new Date()) {
  try {
    const raw = storage?.getItem?.(VO_FLOOR_PROMPT_STORAGE_KEY);
    return String(raw || "") === todayKey(now);
  } catch {
    return false;
  }
}

export function writeVoFloorPromptHidden(storage, now = new Date()) {
  try {
    storage?.setItem?.(VO_FLOOR_PROMPT_STORAGE_KEY, todayKey(now));
    return true;
  } catch {
    return false;
  }
}

export function sanitizeCargoName(raw, max = 32) {
  const s = String(raw || "").trim().replace(/\s+/g, " ");
  if (!s) return "";
  return s.length > max ? `${s.slice(0, max - 1)}…` : s;
}

/** Cargo da presença — a API às vezes manda userCargo, cargo ou user_cargo. */
export function presenceCargoOf(person) {
  if (!person || typeof person !== "object") return "";
  return sanitizeCargoName(
    person.userCargo ||
      person.cargo ||
      person.user_cargo ||
      person.cargo_nome ||
      person.userCargoName
  );
}

export function findSelfPresence(people, { userId, name, identity } = {}) {
  const list = Array.isArray(people) ? people : [];
  const selfId = Number(userId);
  if (Number.isFinite(selfId) && selfId > 0) {
    const byId = list.find((row) => Number(row.userId) === selfId);
    if (byId) return byId;
  }
  const mine = String(identity || "").trim();
  if (mine) {
    const byIdentity = list.find(
      (row) => String(row.twilioIdentity || row.identity || "").trim() === mine
    );
    if (byIdentity) return byIdentity;
  }
  const selfName = String(name || "").trim().toLowerCase();
  if (!selfName) return null;
  return (
    list.find((row) => String(row.userName || "").trim().toLowerCase() === selfName) || null
  );
}

export function floorPresenceCopy({
  self = false,
  name = "",
  status = "present",
  compact = false,
  cargo = "",
  joinedAt = null,
  now = Date.now(),
  timeZone = DEFAULT_CLOCK_TZ,
} = {}) {
  const who = voFloorFirstName(name, self ? "Você" : "Colega");
  const st = normalizeVoCommStatus(status);
  const role = sanitizeCargoName(cargo);
  const statusBit = voCommStatusShortLabel(st);
  const body =
    st === "in_other_meeting"
      ? role
        ? `${role} · em outra sala`
        : "Em outra reunião"
      : statusBit
        ? role
          ? `${role} · ${statusBit}`
          : statusBit.charAt(0).toUpperCase() + statusBit.slice(1)
        : role || "No piso";
  const connected = compact ? "" : floorJoinCaption({ joinedAt, now, timeZone });
  return {
    title: self ? "Você" : who,
    body,
    connected,
  };
}

export function voFloorEmptyHint() {
  return "Nenhuma sala aberta agora. Puxe um café quando quiser conversar.";
}

export function isCafeHuddleTitle(title) {
  return /caf[eé]/i.test(String(title || ""));
}

/** Une clique no assento (tile Twilio ou presença) com a ficha do colega no piso. */
export function resolveVoFloorPerson(people, hint = {}) {
  const list = Array.isArray(people) ? people : [];
  const hintUserId = Number(hint.userId);
  const identity = String(hint.identity || hint.twilioIdentity || "").trim();
  const nameKey = String(hint.name || hint.userName || "")
    .trim()
    .toLowerCase();

  let row = null;
  if (Number.isFinite(hintUserId) && hintUserId > 0) {
    row = list.find((p) => Number(p.userId) === hintUserId) || null;
  }
  if (!row && identity) {
    row =
      list.find(
        (p) => String(p.twilioIdentity || p.identity || "").trim() === identity
      ) || null;
  }
  if (!row && nameKey) {
    row =
      list.find((p) => String(p.userName || "").trim().toLowerCase() === nameKey) ||
      null;
  }

  const fromIdentity = userIdFromAgentIdentity(identity);
  const userId =
    (Number.isFinite(hintUserId) && hintUserId > 0 && hintUserId) ||
    Number(row?.userId) ||
    fromIdentity ||
    null;

  return {
    userId: Number.isFinite(Number(userId)) && Number(userId) > 0 ? Number(userId) : null,
    identity: identity || String(row?.twilioIdentity || row?.identity || "").trim(),
    name: String(hint.name || hint.userName || row?.userName || "").trim() || "Colega",
    avatarUrl: hint.avatarUrl || hint.userPhoto || row?.userPhoto || "",
    cargo: hint.cargo || presenceCargoOf(row) || "",
    status: row?.status || hint.status || "present",
    joinedAt: row?.joinedAt || row?.joined_at || hint.joinedAt || null,
    timezone: row?.timezone || hint.timezone || "",
  };
}

export function nextVoSignalId() {
  return `vo-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
}
